import { QuoteAddress, Address, Region } from '@/store/models';
import { cloneDeep, isInteger, isString, pick, some, get as dataGetter } from 'lodash';
import { findRegionById } from './regionFinder';

const INITIAL_STATE = window.__INITIAL_STATE__ || {};

export function getDefaultCustomAttributes() {
    return [
        {attribute_code: 'tdy_street_number', value: ''},
        {attribute_code: 'tdy_addressbooklabel', value: ''},
        {attribute_code: 'tdy_country_code', value: dataGetter(INITIAL_STATE, 'settings.countrysettings.telprefix', '+39')},
        {attribute_code: 'tdy_extra_address', value: ''},
    ];
}

export function customerAddressPrototype(): Address {
    return {
        id:               0,
        region:           '',
        region_id:        0,
        country_id:       dataGetter(INITIAL_STATE, 'settings.country.default', 'IT'),
        street:           '',
        company:          '',
        telephone:        '',
        postcode:         '',
        city:             '',
        firstname:        '',
        lastname:         '',
        default_shipping: false,
        default_billing:  false,
        custom_attributes: getDefaultCustomAttributes(),
    };
}

// sort to access by indexes in address form
export function sortCustomAttributes(address: any){
    const defCustAttrs = getDefaultCustomAttributes();
    address.custom_attributes.sort((a: any, b: any) => {
        let pa = defCustAttrs.findIndex((ca) => ca.attribute_code === a.attribute_code);
        if (pa === -1) {
            pa = 999;
        }
        let pb = defCustAttrs.findIndex((ca) => ca.attribute_code === b.attribute_code);
        if (pb === -1) {
            pb = 999;
        }
        return pa < pb ? -1 : (pb < pa ? 1 : 0);
    });
    return address;
}

export default function addressNormalizer(address: Address|QuoteAddress): Address|QuoteAddress {
    const addressCopy: Address|QuoteAddress = cloneDeep(address);
    if (address) {
        if (typeof addressCopy.region === 'object' && addressCopy.region !== null) {
            const regionObj = addressCopy.region as Region;
            addressCopy.region = regionObj.region;
            addressCopy.region_id = regionObj.region_id;
        } else if (isInteger(addressCopy.region)) {
            const regionId = addressCopy.region as number;
            addressCopy.region = findRegionById(regionId).region;
            addressCopy.region_id = regionId;
        } else if (isInteger(addressCopy.region_id) && addressCopy.region_id > 0) {
            const foundRegion = findRegionById(addressCopy.region_id);
            addressCopy.region = foundRegion ? foundRegion.region : '';
        }
        if (typeof addressCopy.street === 'string') {
            const street: string = addressCopy.street;
            addressCopy.street = [street];
        }
        if (!addressCopy.country_id) {
            addressCopy.country_id = 'IT';
        }
        delete addressCopy.default_billing;
        delete addressCopy.default_shipping;

        if (!addressCopy.custom_attributes) {
            addressCopy.custom_attributes = getDefaultCustomAttributes();
        }
        sortCustomAttributes(addressCopy);
    }
    return addressCopy;
}

export function quoteAddressToCustomerAddress(address: QuoteAddress): Address {
    const addressCopy = addressNormalizer(address) as any;
    if (isInteger(addressCopy.region_id) && addressCopy.region_id > 0) {
        addressCopy.region = findRegionById(addressCopy.region_id);
        delete addressCopy.region.country_id;
    }

    if (addressCopy.region && isString(addressCopy.region) && !addressCopy.region_id) {
        addressCopy.region = {
            region_code: addressCopy.region,
            region: addressCopy.region,
            region_id: 0,
        };
        if (addressCopy.hasOwnProperty('region_code')) {
            delete addressCopy.region_code;
        }
        if (addressCopy.hasOwnProperty('region_id')) {
            delete addressCopy.region_id;
        }
    }

    if (typeof addressCopy.region === 'object' && addressCopy.region.region_id) {
        if (addressCopy.hasOwnProperty('region_code')) {
            delete addressCopy.region_code;
        }
    }
    if (addressCopy.hasOwnProperty('email')) {
        delete addressCopy.email;
    }
    if (addressCopy.hasOwnProperty('same_as_billing')) {
        delete addressCopy.same_as_billing;
    }
    if (addressCopy.hasOwnProperty('same_as_shipping')) {
        delete addressCopy.same_as_shipping;
    }
    if (addressCopy.hasOwnProperty('customer_address_id')) {
        delete addressCopy.customer_address_id;
    }
    if (addressCopy.hasOwnProperty('save_in_address_book')) {
        delete addressCopy.save_in_address_book;
    }
    if (addressCopy.extension_attributes && addressCopy.extension_attributes.hasOwnProperty('discounts')) {
        delete addressCopy.extension_attributes.discounts;
    }
    if (addressCopy.extension_attributes && addressCopy.extension_attributes.hasOwnProperty('invoicerequest')) {
        delete addressCopy.extension_attributes.invoicerequest;
    }

    return addressCopy as Address;
}

export function normalizeQuoteAddress(address: QuoteAddress, deleteId?: boolean): QuoteAddress {
    address = addressNormalizer(address) as QuoteAddress;
    if (!address.custom_attributes) {
        address.custom_attributes = [];
    }
    const addressCustomAttributes: string[] = address.custom_attributes.map((e: any) => e.attribute_code);
    const defaultCustomAttributes = getDefaultCustomAttributes();
    for (const def of defaultCustomAttributes) {
        if (!addressCustomAttributes.includes(def.attribute_code)) {
            address.custom_attributes.push(def);
        }
    }

    if (address.extension_attributes && address.extension_attributes.hasOwnProperty('discounts')) {
        delete address.extension_attributes.discounts;
    }

    if (deleteId && address.id) {
        if (isString(address.region_id) && address.region_id === '') {
            address.region_id = 0;
        }
        delete address.id;
    }

    return address;
}

/**
 * Check if the given address
 * is into list of addresses given as second parameter
 * @param address
 * @param addresses
 * @return boolean
 */
export function compareAddress(address: Address, addresses: Address[]): boolean {
    const addressHash = JSON.stringify(pick(address, getAddresKeys()));
    if (addresses) {
        return some(addresses, (addr) => addressHash === JSON.stringify(pick(addr, getAddresKeys())));
    }

    return false;
}

/**
 * Check if the given addresses are equals
 * according given fields
 * @param a
 * @param b
 * @return boolean
 */
export function areEqualAddresses(a: Address, b: Address): boolean {
    const aHash = JSON.stringify(pick(a, getAddresKeys()));
    const bHash = JSON.stringify(pick(b, getAddresKeys()));
    return aHash === bHash;
}

export function getStreetNumber(address: Address): string {
    const found = address.custom_attributes.find((att: any) => att.attribute_code === 'tdy_street_number');
    return found ? found.value : '';
}

export function getAddressbookLabel(address: Address): string {
    const found = address.custom_attributes.find((att: any) => att.attribute_code === 'tdy_addressbooklabel');
    return found ? found.value : '';
}

export function getExtraAddress(address: Address): string {
    const found = address.custom_attributes.find((att: any) => att.attribute_code === 'tdy_extra_address');
    return found ? found.value : '';
}

export function copyAddressData(from: any, to: any) {
    getAddresKeys().forEach((key: string) => {
        to[key] = from[key];
    });
    const streetNum = to.custom_attributes.find((att: any) => att.attribute_code === 'tdy_street_number');
    if (streetNum) {
        streetNum.value = getStreetNumber(from);
    }
    const addressbookLabel = to.custom_attributes.find((att: any) => att.attribute_code === 'tdy_addressbooklabel');
    if (addressbookLabel) {
        addressbookLabel.value = getAddressbookLabel(from);
    }
    const extraAddress = to.custom_attributes.find((att: any) => att.attribute_code === 'tdy_extra_address');
    if (extraAddress) {
        extraAddress.value = getExtraAddress(from);
    }
}

export function isAddressFulfilled(address: QuoteAddress|Address): boolean {
    if (!address.firstname
        || !address.lastname
        || !address.postcode
        || !address.city
        || !address.country_id
        || !address.telephone
        || !address.street

    ) {
        return false;
    }
    return true;
}

export function getAddresKeys(): string[] {
    return ['city', 'country_id', 'firstname', 'lastname', 'postcode', 'region_id', 'street', 'telephone'];
}
